import { arktypeResolver } from "@hookform/resolvers/arktype";
import { LoaderCircle, Save } from "lucide-react";
import { useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";

import type { PersonDetail } from "@/api/db/people";
import { UnsavedChangesGuard } from "@/components/shared/dialogs/unsaved-changes-guard";
import { Button } from "@/components/ui/button";
import { useSavePerson } from "@/hooks/use-save-person";
import { CadastroFormSchemas, displayDraftFromPerson, draftToInput } from "@/lib/cadastro-draft";
import { PessoaLabels } from "@/lib/labels";

import { FichaRegistroFields } from "./ficha-registro-fields";

const REG = PessoaLabels.registry;

export function FichaRegistro({ person }: { person: PersonDetail }) {
	const { save, isPending, isError } = useSavePerson(() => {});
	const methods = useForm<typeof CadastroFormSchemas.draft.infer>({
		resolver: arktypeResolver(CadastroFormSchemas.draft),
		mode: "onChange",
		defaultValues: displayDraftFromPerson(person),
	});
	const {
		formState: { isDirty, isValid },
		getValues,
		handleSubmit,
		reset,
	} = methods;
	const personId = useRef(person.id);

	useEffect(() => {
		if (personId.current === person.id) {
			return;
		}

		personId.current = person.id;
		reset(displayDraftFromPerson(person));
	}, [person, reset]);

	const submit = handleSubmit((draft) => {
		const input = draftToInput(draft, person.id);
		const submittedRevision = JSON.stringify(input);

		save(input, {
			onSuccess: () => {
				const changedWhileSaving =
					JSON.stringify(draftToInput(getValues(), person.id)) !== submittedRevision;

				reset(draft, { keepValues: changedWhileSaving });
			},
		});
	});

	return (
		<FormProvider {...methods}>
			<form data-component="ficha-registro" data-dirty={isDirty} onSubmit={submit}>
				<FichaRegistroFields person={person} />

				{isDirty && (
					<div className="workspace-save">
						{isError && (
							<p className="rounded-full border border-destructive/20 bg-card px-4 py-2 text-sm text-destructive shadow-overlay">
								{REG.saveError}
							</p>
						)}

						<Button
							type="submit"
							variant="default"
							size="sm"
							data-slot="save"
							disabled={!isValid || isPending}
							className="gap-2 border border-background/15 px-8 py-4 text-base shadow-control [&_svg]:size-5"
						>
							{isPending && <LoaderCircle className="animate-spin" />}
							{!isPending && <Save />}
							{isPending && REG.saving}
							{!isPending && REG.save}
						</Button>
					</div>
				)}
			</form>

			<UnsavedChangesGuard
				blocking={isDirty}
				title={REG.leaveTitle}
				description={REG.leaveDescription}
				stayLabel={REG.stay}
				leaveLabel={REG.leave}
			/>
		</FormProvider>
	);
}
